import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRinging, Check } from "@phosphor-icons/react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/util";

// Bell icon with unread badge. Polls /api/notifications every 30s and drops
// down a scrolling list of the most recent 50 entries. Clicking a row marks
// it read and routes to `url`.
export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 50 } });
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (_) { /* ignore — logged out or transient */ }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const off = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", off);
    return () => document.removeEventListener("mousedown", off);
  }, [open]);

  const clickRow = async (n) => {
    setOpen(false);
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch (_) { /* ignore */ }
      setUnread((u) => Math.max(0, u - 1));
    }
    if (n.url) nav(n.url);
  };
  const markAll = async () => {
    setBusy(true);
    try {
      await api.post("/notifications/mark-all-read");
      setUnread(0);
      setItems((rows) => rows.map((r) => ({ ...r, read: true })));
    } finally { setBusy(false); }
  };

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="notif-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative w-9 h-9 rounded-xl flex items-center justify-center text-slate-700 hover:text-amber-700 hover:bg-white/60 shrink-0"
        aria-label={`Notifications (${unread} unread)`}
        title={`${unread} unread notifications`}
      >
        {unread > 0 ? <BellRinging size={18} weight="fill" className="text-amber-700" /> : <Bell size={18} weight="bold" />}
        {unread > 0 && (
          <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-black flex items-center justify-center leading-none shadow-sm">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notif-panel" className="absolute right-0 mt-2 w-[340px] max-w-[92vw] rounded-2xl glass border border-white/60 shadow-xl overflow-hidden z-[200]">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/40 bg-white/40">
            <div className="font-heading font-black text-sm text-slate-800">Notifications</div>
            {unread > 0 && (
              <button data-testid="notif-mark-all" onClick={markAll} disabled={busy}
                      className="text-[11px] font-semibold text-amber-800 hover:text-amber-950 inline-flex items-center gap-1">
                <Check size={11} weight="bold" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {items.length === 0 && (
              <div className="px-4 py-8 text-center text-xs text-slate-500">
                No notifications yet.
                <div className="mt-1 text-slate-400">You&apos;ll see approvals, payment updates, and admin actions here.</div>
              </div>
            )}
            {items.map((n) => (
              <button
                key={n.id}
                data-testid={`notif-item-${n.id}`}
                onClick={() => clickRow(n)}
                className={`w-full text-left px-4 py-3 border-b border-white/30 last:border-b-0 hover:bg-amber-50/50 transition-colors ${n.read ? "" : "bg-amber-50/25"}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-600 flex-shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className={`text-xs ${n.read ? "font-medium text-slate-700" : "font-bold text-slate-900"} truncate`}>{n.title}</div>
                    <div className="text-[11px] text-slate-600 mt-0.5 line-clamp-2">{n.body}</div>
                    <div className="text-[10px] text-slate-400 mt-1 font-mono">{fmtDate(n.created_at)}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
